import {
  PrefixIcon,
  PrefixIconColorKeys,
  PrefixIconSizeKeys,
  TextAppearanceKeys,
  TextEmphasisKeys,
  TextSizeKeys,
} from '@/constants/enum';
import { IStateCheckoutRule } from '@/pages/Checkout/components/CreateUpdate';
import { IDefaultOpenModalAddCondition } from '@/pages/Settings/Components/EstimateRules/components/CURule/components/DisplayConditions';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { RelationCondition } from '@/types/newSetup.type';

export type ICheckoutRuleErrorField =
  | 'product'
  | 'exceptionProduct'
  | 'collection'
  | 'vendor'
  | 'zipcode'
  | 'country'
  | 'state'
  | 'ruleName'
  | 'city'
  | 'productTags'
  | 'skus'
  | 'inventoryLocations'
  | 'serverError'
  | 'productMetafieldItem'
  | 'variantMetafieldItem';
export interface ICheckoutRuleError {
  field: ICheckoutRuleErrorField;
  msg: string;
}

// Define the initial state using that type
export const initCheckoutRuleState: IStateCheckoutRule = {
  name: '',
  typeRelationOfItemCondition: RelationCondition.OR,
  shippingMethods: [],
  enable: true,
  messageText: '{delivery_min} and {delivery_max}',
  minPreparationDays: 0,
  maxPreparationDays: 6,
  minDeliveryDays: 0,
  maxDeliveryDays: 8,
  iconId: PrefixIcon.none,
  textSize: TextSizeKeys.base,
  textAppearance: TextAppearanceKeys.base,
  iconSize: PrefixIconSizeKeys.base,
  iconColor: PrefixIconColorKeys.base,
  textEmphasis: TextEmphasisKeys.base,
  checkoutItemConditions: [],
  checkoutLocationConditions: [],
};

const initError: ICheckoutRuleError[] = [];

export const checkoutRuleSlice = createSlice({
  name: 'checkoutRule',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState: {
    state: initCheckoutRuleState,
    isDefaultOpenModalAddCondition: {
      product: false,
      collection: false,
      vendor: false,
      inventoryLocations: false,
      productTags: false,
      skus: false,
      exception: false,
    },
    errors: initError,
    currentCount: 0,
  },
  reducers: {
    handleChange: (
      state,
      action: PayloadAction<{
        key: keyof IStateCheckoutRule;
        value: IStateCheckoutRule[keyof IStateCheckoutRule];
      }>,
    ) => {
      state.state = {
        ...state.state,
        [action.payload.key]: action.payload.value,
      };

      const serverError = state.errors.find((error) => error.field === 'serverError');
      if (serverError) {
        state.errors = state.errors.filter((error) => error.field !== 'serverError');
      }
    },
    setValues: (
      state,
      action: PayloadAction<{
        value: IStateCheckoutRule;
        currentCount?: number;
      }>,
    ) => {
      state.state = action.payload.value;
      if (action.payload.currentCount !== undefined) state.currentCount = action.payload.currentCount + 1;
    },
    handleInit: (state) => {
      state.state = initCheckoutRuleState;
      state.errors = initError;
      state.currentCount = 0;
    },
    handleSetError: (state, action: PayloadAction<{ value: ICheckoutRuleError }>) => {
      let temp = [...state.errors];
      if (action.payload.value.msg) {
        const index = temp.findIndex((item) => item.field === action.payload.value.field);
        if (index !== -1) {
          temp[index] = action.payload.value;
        } else {
          temp.push(action.payload.value);
        }
      } else {
        temp = temp.filter((item) => item.field !== action.payload.value.field);
      }
      state.errors = temp;
    },
    clearError: (state) => {
      state.errors = [];
    },
    handleSetIsDefaultOpenModalAddCondition: (state, action: PayloadAction<IDefaultOpenModalAddCondition>) => {
      state.isDefaultOpenModalAddCondition = action.payload;
    },
    handleResetIsDefaultOpenModalAddCondition: (state) => {
      state.isDefaultOpenModalAddCondition = {
        product: false,
        collection: false,
        vendor: false,
        inventoryLocations: false,
        productTags: false,
        skus: false,
        exception: false,
      };
    },
    handleAddCurrentCount: (state) => {
      state.currentCount = state.currentCount + 1;
    },
    handleSetCountValue: (state, action: PayloadAction<number>) => {
      state.currentCount = action.payload;
    },
  },
});

// Other code such as selectors can use the imported `RootState` type

export const checkoutRuleSettingsSelector = createSelector(
  (state: RootState) => state.checkoutRule.state,
  (state) => state,
);
export const initCheckoutRuleSettingsSelector = createSelector(
  () => initCheckoutRuleState,
  (state) => state,
);

export const checkoutRuleErrorsSelector = createSelector(
  (state: RootState) => state.checkoutRule.errors,
  (state) => state,
);

export const isDefaultOpenModalAddConditionCheckoutSelector = createSelector(
  (state: RootState) => state.checkoutRule.isDefaultOpenModalAddCondition,
  (state) => state,
);

export const currentOrderItemValueSelector = createSelector(
  (state: RootState) => state.checkoutRule.currentCount,
  (state) => state,
);

export default checkoutRuleSlice;
